import { useState, useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { FaListOl, FaPercentage, FaFilter, FaChartLine } from 'react-icons/fa';

import {
    formatDateBucket,
    generateDateBuckets,
    getAutoResolution,
} from '../../../common/utils';

import TableMetric from '../../../containers/Reports/metric';
import BarLineCombo from '../../Charts/Combo/barLine';
import DoughnutCard from '../../Charts/Doughnut/card';


function toDate(value) {
    if (!value) return null;

    const dt = new Date(value?.match?.('T') ? value : `${value}T12:00:00`);
    return isNaN(dt) ? null : dt;
}

function isWithinRange(date, startDate, endDate) {
    if (!date || !startDate || !endDate) return false;

    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59`);

    return date >= start && date <= end;
}

function formatPercent(num, den) {
    if (!den) return '0%';
    return `${Math.round((num / den) * 100)}%`;
}

function transformStepBreakdownToDonutData(breakdown = {}) {
    return Object.entries(breakdown).map(([label, value]) => ({
        label,
        value,
    }));
}

export default function PipelineConversion({ data = [], filters = {} }) {
    const [resolution, setResolution] = useState('monthly');
    const [summaryData, setSummaryData] = useState({
        cohortCount: 0,
        convertedCount: 0,
        conversionRate: 0,
        stepCounts: {},
        stepRates: {},
        stepBreakdown: {},
        trendData: [],
    });

    const [metrics, setMetrics] = useState([
        {
            label: 'Cohort',
            value: 0,
            tooltip: '# of records in the selected cohort',
            icon: <FaListOl style={{ color: '#ff7a59', marginRight: 8 }} />,
        },
        {
            label: 'Converted',
            value: 0,
            tooltip: '# of records reaching the final selected milestone',
            icon: <FaChartLine style={{ color: '#ff7a59', marginRight: 8 }} />,
        },
        {
            label: 'Rate',
            value: '0%',
            tooltip: 'Conversion rate from cohort to final selected milestone',
            icon: <FaPercentage style={{ color: '#ff7a59', marginRight: 8 }} />,
        },
        {
            label: 'Steps',
            value: 0,
            tooltip: '# of milestones in the selected funnel',
            icon: <FaFilter style={{ color: '#ff7a59', marginRight: 8 }} />,
        },
    ]);

    const getConversionSummaryData = useCallback(() => {
        const cohortField = filters?.cohortField || 'oppDate';
        const funnelSteps = Array.isArray(filters?.funnelSteps) && filters.funnelSteps.length
            ? filters.funnelSteps
            : [ cohortField, 'fundedDate' ];

        const finalStep = funnelSteps[funnelSteps.length - 1];

        const summary = {
            cohortCount: 0,
            convertedCount: 0,
            conversionRate: 0,
            stepCounts: {},
            stepRates: {},
            stepBreakdown: {},
        };

        funnelSteps.forEach((step) => {
            summary.stepCounts[step] = 0;
            summary.stepRates[step] = 0;
        });

        const trendMap = {};
        const allBuckets = generateDateBuckets(filters.startDate, filters.endDate, resolution);

        for (const bucket of allBuckets) {
            trendMap[bucket] = {
                x: bucket,
                units: 0,
                rate: 0,
            };
        }

        const cohortRecords = [];

        for (const record of data) {
            const milestones = record?.milestones || {};
            const cohortDate = toDate(milestones[cohortField] || record?.[cohortField]);

            if (!isWithinRange(cohortDate, filters.startDate, filters.endDate)) continue;

            summary.cohortCount += 1;
            cohortRecords.push(record);

            let priorStepsSatisfied = true;

            for (const step of funnelSteps) {
                const stepDate = toDate(milestones[step] || record?.[step]);

                if (priorStepsSatisfied && stepDate) {
                    summary.stepCounts[step] += 1;
                } else {
                    priorStepsSatisfied = false;
                }
            }

            const finalStepDate = toDate(milestones[finalStep] || record?.[finalStep]);
            if (priorStepsSatisfied && finalStepDate) {
                summary.convertedCount += 1;
            }
        }

        for (const step of funnelSteps) {
            summary.stepRates[step] = summary.cohortCount
                ? (summary.stepCounts[step] / summary.cohortCount)
                : 0;
        }

        summary.conversionRate = summary.cohortCount
            ? (summary.convertedCount / summary.cohortCount)
            : 0;

        for (const record of cohortRecords) {
            const milestones = record?.milestones || {};
            const finalStepDate = toDate(milestones[finalStep] || record?.[finalStep]);

            if (!finalStepDate) continue;

            const bucket = formatDateBucket(finalStepDate, resolution);

            if (trendMap[bucket]) {
                trendMap[bucket].units += 1;
            }
        }

        Object.keys(trendMap).forEach((bucket) => {
            trendMap[bucket].rate = summary.cohortCount
                ? +(trendMap[bucket].units / summary.cohortCount * 100).toFixed(1)
                : 0;
        });

        summary.stepBreakdown = funnelSteps.reduce((acc, step) => {
            acc[step] = summary.stepCounts[step];
            return acc;
        }, {});

        return {
            summaryData: summary,
            trendData: Object.values(trendMap).sort((a, b) => new Date(a.x) - new Date(b.x)),
            funnelSteps,
            finalStep,
        };
    }, [data, filters, resolution]);

    useEffect(() => {
        if (filters?.startDate && filters?.endDate) {
            setResolution(getAutoResolution(filters.startDate, filters.endDate));
        }
    }, [filters]);

    useEffect(() => {
        if (data?.length && filters?.startDate && filters?.endDate) {
            const summary = getConversionSummaryData();
            setSummaryData({
                ...summary.summaryData,
                trendData: summary.trendData,
                funnelSteps: summary.funnelSteps,
                finalStep: summary.finalStep,
            });
        } else {
            setSummaryData({
                cohortCount: 0,
                convertedCount: 0,
                conversionRate: 0,
                stepCounts: {},
                stepRates: {},
                stepBreakdown: {},
                trendData: [],
                funnelSteps: [],
                finalStep: null,
            });
        }
    }, [data, filters, getConversionSummaryData]);

    useEffect(() => {
        const funnelSteps = filters?.funnelSteps || [];

        setMetrics([
            {
                label: 'Cohort',
                value: summaryData.cohortCount || 0,
                tooltip: '# of records in the selected cohort',
                icon: <FaListOl style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Converted',
                value: summaryData.convertedCount || 0,
                tooltip: '# of records reaching the final selected milestone',
                icon: <FaChartLine style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Rate',
                value: formatPercent(summaryData.convertedCount, summaryData.cohortCount),
                tooltip: 'Conversion rate from cohort to final selected milestone',
                icon: <FaPercentage style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
            {
                label: 'Steps',
                value: funnelSteps.length || 0,
                tooltip: '# of milestones in the selected funnel',
                icon: <FaFilter style={{ color: '#ff7a59', marginRight: 8 }} />,
            },
        ]);
    }, [summaryData, filters]);

    return (
        <Container fluid className='w-100'>
            <Row className='pb-4 text-center'>
                <Col className='ps-0' sm={2}>
                    <Box
                        className='h-100'
                        display="flex"
                        gap={2}
                        justifyContent="space-evenly"
                        sx={{ flexDirection: { xs: 'column', md: 'row' } }}
                    >
                        <Card
                            variant="outlined"
                            className='py-1 px-0 me-2 w-100'
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            <Stack
                                direction="column"
                                justifyContent="space-evenly"
                                spacing={0}
                                sx={{ width: 1, flex: 1, minHeight: 0 }}
                                divider={<Divider orientation="horizontal" flexItem style={{ backgroundColor: 'grey' }} />}
                            >
                                {
                                    metrics.map((metric, idx) => (
                                        <TableMetric
                                            key={`metric-${idx}`}
                                            metric={metric}
                                            idx={idx}
                                            showDivider={idx < (metrics.length - 1)}
                                            direction='vertical'
                                        />
                                    ))
                                }
                            </Stack>
                        </Card>
                    </Box>
                </Col>

                <Col className='px-0'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">
                                        Conversion Trends
                                    </Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={{ height: '400px' }}>
                                    <BarLineCombo
                                        data={summaryData.trendData || []}
                                        height={400}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='pb-4'>
                <Col className='px-0'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">
                                        Funnel Step Distribution
                                    </Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={12} md={4}>
                                    <DoughnutCard
                                        centerLabel="Steps"
                                        data={transformStepBreakdownToDonutData(summaryData.stepBreakdown ?? {})}
                                        mode="units"
                                        height={200}
                                    />
                                </Col>

                                <Col sm={12} md={8}>
                                    <div className="px-3 py-2">
                                        {(summaryData.funnelSteps || []).map((step, idx) => (
                                            <div
                                                key={`step-row-${step}`}
                                                className="d-flex justify-content-between align-items-center py-2"
                                                style={{
                                                    borderBottom: idx < summaryData.funnelSteps.length - 1
                                                        ? '1px solid #eee'
                                                        : 'none'
                                                }}
                                            >
                                                <div>
                                                    <strong>{step}</strong>
                                                </div>
                                                <div className="text-end">
                                                    <div>{summaryData.stepCounts?.[step] ?? 0}</div>
                                                    <div style={{ fontSize: 12, color: '#666' }}>
                                                        {formatPercent(
                                                            summaryData.stepCounts?.[step] ?? 0,
                                                            summaryData.cohortCount ?? 0
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}